<script lang="ts">
	//Services
	import { postReq, standardLogin } from '../../../services/http';
	import { format } from '../../../services/i18n';
	import sha1 from 'crypto-js/sha1';
	//Helpers
	import { passwordHasAllMandatorySymbols } from '../../../helpers/utilities';
	import { validateAllowedSymbols } from '../../../helpers/validators';
	//Design system components
	import { ButtonV2, InputV2, Text, toast } from '@Shelly/shelly-design-system';
	import type { InputPropsTypes } from '@Shelly/shelly-design-system';
	// Types
	import PasswordUtility from 'src/components/PasswordUtilityV2.svelte';
	import type { ChangePasswordRequest } from '../../../types/requests/settings/user/change_password';
	import { user_data } from 'src/stores/account/user';
	import { profiles } from 'src/stores/features/profiles';
	import { capFirstChar } from 'src/helpers/utilities';
	import { showForceLogoutAlert } from 'src/services/auth_errors';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	let currentPassValue = '',
		currentPassError: string = '',
		newPassError: string = '';

	export let isMobile = false;

	let inputProps: {
		size: InputPropsTypes['size'];
		icon: InputPropsTypes['icon'] | null;
		value: InputPropsTypes['value'];
		label: InputPropsTypes['label'];
		type: InputPropsTypes['type'];
		disabled: InputPropsTypes['disabled'];
		useV2Components: boolean;
		class: string;
		autocomplete: string;
	} = {
		icon: null,
		size: 'l',
		class: '',
		disabled: false,
		value: '',
		label: '',
		type: 'password',
		useV2Components: true,
		autocomplete: 'new-password',
	};

	$: error = newPassError;

	const handleChangePassword = async (oldPass, newPass) => {
		if (validateAllowedSymbols(newPass)) {
			newPassError = $format('password_validate_allowed_symbols', {
				default:
					'Please use only letters (a – z and A – Z), numbers (0 – 9) and uppercase characters such as !@#$%^&*.()',
			});
			return;
		}

		const res: ChangePasswordRequest = await postReq('/user/change_password', {
			old_password: window.btoa(oldPass),
			new_password: window.btoa(newPass),
		});

		if (res.isok) {
			// Login migration: Relogin to obtain new tokens
			let res = await standardLogin($user_data.email, sha1(newPass).toString());
			if (!res.isok) {
				//Edge case: Relogin failed, but change password was successful
				// Current tokens are invalid, no other option than logout
				return await showForceLogoutAlert();
			}

			const activeProfile = $profiles.find((item) => item.isSelected);
			activeProfile.pass = sha1(newPass).toString();

			profiles.edit(activeProfile);
			toast.open({
				type: 'success',
				title: $format('setting_scc_saved_toast'),
			});
		} else {
			if (res?.errors?.wrong_old_password) {
				currentPassError = $format('toast_error_password_change_wrong_current_pass', {
					default: 'Wrong password.',
				});
			} else if (res.errors?.spwd) {
				newPassError = $format('toast_error_password_change_same_password', {
					default: 'Choose a password you haven’t used before.',
				});
			} else {
				toast.open({ type: 'danger', title: $format('setting_err_toast') });
			}
		}
	};

	const clearErrors = () => {
		if (currentPassError) {
			currentPassError = '';
		}
		if (newPassError) {
			newPassError = '';
		}
	};
</script>

<div
	class={classMap({
		'd-flex align-items-start gap-m justify-content-between mb-m': true,
		'flex-direction-column': isMobile,
	})}
>
	<div class="width-full">
		<Text styleType="body-medium" class="mb-s">
			{capFirstChar($format('placeholder_current_password'))}
		</Text>
		<InputV2
			class="width-full"
			backgroundColor="color-00"
			icon={null}
			disableToggleVisibility
			aria-label={capFirstChar($format('placeholder_current_password'))}
			size="l"
			bind:value={currentPassValue}
			type={'password'}
			error={currentPassError}
			on:click={() => clearErrors()}
			id="input-ph5v21d"
		/>
	</div>

	<div class="width-full">
		<Text styleType="body-medium" class="mb-s">
			{capFirstChar($format('placeholder_new_password'))}
		</Text>
		<PasswordUtility bind:inputProps bind:error on:click={() => clearErrors()} />
	</div>
</div>

<div class="d-flex justify-content-end">
	<ButtonV2
		size="m"
		variant="primary"
		class={classMap({
			'width-s': !isMobile,
			'width-full': isMobile,
		})}
		disabled={!passwordHasAllMandatorySymbols(inputProps.value)}
		on:click={() => handleChangePassword(currentPassValue, inputProps.value)}
	>
		{$format('button_save')}
	</ButtonV2>
</div>

